import React, { memo, useRef, useState, useCallback, useEffect } from "react";
import styles from "./FileUploadInput.module.scss";
import { UseFormRegisterReturn } from "react-hook-form";
import { toast } from "react-toastify";

export enum StyleOfFileInput {
	ButtonFirst = "buttonFirst",
	FormInput = "formInput",
}

type Props = {
	children?: React.ReactNode;
	styleOfInput?: StyleOfFileInput;
	initialFiles?: string[];
	registerInputProps?: UseFormRegisterReturn<any>;
	maxFileSize: number;
	multiple?: boolean;
	required?: boolean;
	accept?: string;
	label?: string;
	error?: string;
	onFilesSelect: (files: File[]) => void;
};

const FileUploadInput: React.FC<Props> = memo((props: Props) => {
	const { children, onFilesSelect, registerInputProps, multiple, accept, initialFiles, maxFileSize, label, error, required } = props;

	const fileInputRef = useRef<HTMLInputElement>(null);

	const [fileNames, setFileNames] = useState<string[]>([]);

	const handleFileSelect = useCallback(() => {
		fileInputRef.current?.click();
	}, [fileInputRef]);

	const fileSizeLimit = maxFileSize * 1024 * 1024;

	const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
		const fileNames: string[] = [];
		const files: File[] = [];

		if (event.target.files) {
			for (let i = 0; i < event.target.files!.length; i++) {
				if (event.target.files![i].size > fileSizeLimit) {
					toast.warn(`File size is too large. Maximum file size is ${maxFileSize}MB`);
					return;
				}
				fileNames.push(event.target.files![i].name);
				files.push(event.target.files![i]);
			}
		}

		setFileNames(fileNames);
		onFilesSelect(files);
	};

	useEffect(() => {
		setFileNames(initialFiles ?? []);
	}, [initialFiles]);

	const handleKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
		if (event.key === "Enter" || event.key === " ") {
			handleFileSelect();
		}
	};

	return (
		<div>
			<div className={`${styles.inputContainer} ${error ? styles.errorInput : ''}`} onClick={handleFileSelect} onKeyDown={handleKeyDown} role="button" tabIndex={0} aria-label="File input">
				<div className={`${styles.inputIcon} ${styles.orangeBg}`}>{children}</div>
				<div className={styles.inputContent}>
					<span className={styles.inputLabel}>
						{label} {required && <div className={styles.asterix}>&nbsp;*</div>}
					</span>
					{fileNames.map((fileName, index) => (
						<div className={styles.fileNameElement} key={fileName + index}>
							<p className={styles.fileName}>{fileName ?? "No file selected"}</p>
						</div>
					))}
					{fileNames.length === 0 && <span className={styles.filetype}>Jpeg, Png, Pdf, 24mb max file</span>}
					<input type="file" accept={accept} multiple={multiple} {...registerInputProps} ref={fileInputRef} onChange={handleFileChange} hidden />
				</div>
				<button type="button" className={styles.button}>Select File</button>
			</div>
			{error && <div className={styles.error}>{error}</div>}
		</div>
	);
});

export default FileUploadInput;
